import { handleKeyboardClick } from '@/utils/accessibility';
import { getNormalizedPathname } from '@/utils/locale';
import { getActionAriaLabel, isPathMatch } from '@/utils/urlUtils';

import { Box } from '@/components/Box';
import { Flex } from '@/components/Flex';
import { useCompoundContext } from '@/components/Header/context';
import LinkWithLocale from '@/components/LinkWithLocale/LinkWithLocale';
import { Typography } from '@/components/Typography';
import { useCheckout } from '@/hooks/useCheckout';
import { useRouter } from '@/hooks/useRouter';
import { useUser } from '@/hooks/useUser';
import { useWishList } from '@/hooks/useWishlist';
import { preloadExpressCheckoutChunks } from '@/lib/preloadExpressCheckoutChunks';
import { useRegionProvider } from '@/providers/RegionProvider';
import { searchStateActions } from '@/states/searchState';
import type { HeaderThemeKeys } from '@/theme/types/components';

import {
  preloadSearchOverlay,
  preloadShoppingBagDrawer,
} from '../../lazyOverlays';

import * as S from './NavigationMenu.styles';

export interface NavigationMenuProps {
  handleCartClick?: () => void;
  headerBackground: HeaderThemeKeys;
  isShrunk?: boolean;
  textColorOption?: string;
}

export const NavigationMenu = ({
  handleCartClick,
  headerBackground,
  isShrunk = true,
  textColorOption = 'dark',
}: NavigationMenuProps) => {
  const { asPath, pathname, paths } = useRouter();
  const isShoppingBag = isPathMatch(
    getNormalizedPathname(pathname),
    paths.store.shoppingBag.asPath(),
  );
  const { locale, productCatalogMode } = useRegionProvider();
  const { country } = useRegionProvider();
  const {
    snapshot: { isProcessing, linesCount },
  } = useCheckout();
  const {
    snapshot: { isAuthenticated },
  } = useUser();
  useCompoundContext();
  const { isHighlighted } = useWishList({ countryCode: country.code });

  const isBagEmpty = linesCount === 0;
  const CartIcon = () => {
    return (
      <Flex data-test-id='NavigationMenuCartIcon'>
        <S.CartIcon
          $headerBackground={headerBackground}
          $isBagEmpty={!isBagEmpty}
          $isShrunk={isShrunk}
          $textColorOption={textColorOption}
          name='bag'
          style={{ cursor: isProcessing ? 'wait' : 'pointer' }}
        />

        <Typography data-test-id='NavigationMenuCartIconTypography' scale='p3'>
          {!isBagEmpty ? linesCount : null}
        </Typography>
      </Flex>
    );
  };
  const cartLink = isProcessing ? (
    <CartIcon />
  ) : (
    <Box
      aria-label={
        getActionAriaLabel('open_shopping_bag', locale.code) ||
        'Open shopping bag'
      }
      data-test-id='NavigationMenuCartLink'
      onClick={(evt) => {
        if (isProcessing) {
          evt.preventDefault();
        }

        handleCartClick && handleCartClick();
      }}
      onFocus={preloadExpressCheckoutChunks}
      onKeyDown={(e) => handleKeyboardClick(e, { disabled: isProcessing })}
      onMouseEnter={preloadExpressCheckoutChunks}
      role='button'
      style={{ textDecoration: 'none' }}
      tabIndex={0}
    >
      <CartIcon />
    </Box>
  );

  const handleSearchClick = () => {
    if (asPath?.includes('/search/')) {
      return;
    }
    searchStateActions.toggleOverlayStatus();
  };

  return (
    <S.NavigationMenu data-test-id='NavigationMenu'>
      <li className='navigationmenu-search' data-test-id='NavigationMenuSearch'>
        <S.SearchIcon
          $headerBackground={headerBackground}
          $isShrunk={isShrunk}
          $textColorOption={textColorOption}
          aria-label={
            getActionAriaLabel('open_search', locale.code) || 'Open search'
          }
          data-test-id='NavigationMenuSearchIcon'
          data-testid='NavigationMenuSearchIcon'
          name='search'
          onClick={handleSearchClick}
          onFocus={preloadSearchOverlay}
          onKeyDown={handleKeyboardClick}
          onPointerEnter={preloadSearchOverlay}
          role='button'
          tabIndex={0}
        />
      </li>
      <li data-test-id='NavigationMenuWishlist'>
        <LinkWithLocale
          aria-label={
            getActionAriaLabel('view_wishlist', locale.code) ||
            'View your wishlist'
          }
          href='/my-account/my-wishlist/'
          tabIndex={0}
        >
          <S.HeartOutline
            $headerBackground={headerBackground}
            $isHeightLight={isHighlighted}
            $isShrunk={isShrunk}
            $textColorOption={textColorOption}
            data-test-id='NavigationMenuWishlistIcon'
            name='heartOutline'
          />
        </LinkWithLocale>
      </li>
      <li data-test-id='NavigationMenuAccount'>
        <LinkWithLocale
          aria-label={
            isAuthenticated
              ? getActionAriaLabel('my_account', locale.code) || 'My account'
              : getActionAriaLabel('sign_in', locale.code) ||
                'Sign in to your account'
          }
          href={
            isAuthenticated
              ? paths.myAccount.asPath()
              : paths.myAccount.login.url({
                  query: { next: paths.myAccount.asPath() },
                })
          }
          tabIndex={0}
        >
          <S.AccountIcon
            $headerBackground={headerBackground}
            $isShrunk={isShrunk}
            $textColorOption={textColorOption}
            data-test-id='NavigationMenuAccountIcon'
            name='account'
          />
        </LinkWithLocale>
      </li>
      {!productCatalogMode && !isShoppingBag && (
        <li
          data-test-id='NavigationMenuCartLinkContainer'
          onFocus={preloadShoppingBagDrawer}
          onPointerEnter={preloadShoppingBagDrawer}
        >
          {cartLink}
        </li>
      )}
    </S.NavigationMenu>
  );
};

NavigationMenu.displayName = 'NavigationMenu';
